import { BookingRequestPayload, BookingResponse } from '../types/booking'

/** Existing Google Apps Script Web App (POST, JSON in / JSON out). */
export const APPS_SCRIPT_URL =
  'https://script.google.com/macros/s/AKfycbzbcwrq8WKTHOcAsktFUJfYPT2whZdGlCYKb-aShnnH9g27YW9LAA2gmfYBFR6FBA-VyQ/exec'

/** Vercel serverless proxy (api/booking.ts). Reads APPS_SCRIPT_URL from the server environment. */
export const PROXY_PATH = '/api/booking'

export class BookingApiError extends Error {
  constructor(
    message: string,
    public readonly kind: 'network' | 'rejected' | 'invalid-response',
  ) {
    super(message)
  }
}

type PostResult = { kind: 'json'; data: unknown } | { kind: 'not-json' }

async function post(url: string, payload: BookingRequestPayload, contentType: string): Promise<PostResult> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': contentType },
    body: JSON.stringify(payload),
    redirect: 'follow',
  })
  const text = await response.text()
  try {
    return { kind: 'json', data: JSON.parse(text) }
  } catch {
    return { kind: 'not-json' }
  }
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)
  return undefined
}

function toText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

function normalise(data: unknown): BookingResponse {
  if (!data || typeof data !== 'object') {
    throw new BookingApiError('The booking system sent an unexpected response.', 'invalid-response')
  }
  const raw = data as Record<string, unknown>
  return {
    ok: raw.ok === true || raw.ok === 'true',
    bookingId: toText(raw.bookingId),
    bookingStatus: toText(raw.bookingStatus),
    paymentStatus: toText(raw.paymentStatus),
    message: toText(raw.message),
    currency: toText(raw.currency),
    baseAmount: toNumber(raw.baseAmount),
    earlyFee: toNumber(raw.earlyFee),
    lateFee: toNumber(raw.lateFee),
    cautionFee: toNumber(raw.cautionFee),
    totalAmount: toNumber(raw.totalAmount),
  }
}

/**
 * Sends a booking request. Tries the server-side proxy first (deployed on Vercel);
 * when no proxy exists (it returns non-JSON), posts straight to Apps Script using a
 * "simple" text/plain request, which avoids a CORS preflight.
 * Never fabricates a success — any failure is thrown.
 */
export async function submitBookingRequest(payload: BookingRequestPayload): Promise<BookingResponse> {
  let result: PostResult | null = null

  try {
    result = await post(PROXY_PATH, payload, 'application/json')
  } catch {
    result = null
  }

  if (!result || result.kind === 'not-json') {
    try {
      result = await post(APPS_SCRIPT_URL, payload, 'text/plain;charset=utf-8')
    } catch {
      throw new BookingApiError(
        'We couldn’t reach the booking system. Check your connection and try again — nothing has been booked.',
        'network',
      )
    }
  }

  if (result.kind === 'not-json') {
    throw new BookingApiError('The booking system sent an unexpected response. Please try again.', 'invalid-response')
  }

  const response = normalise(result.data)
  if (!response.ok) {
    throw new BookingApiError(
      response.message ?? 'The booking system could not accept this request. Please review your details.',
      'rejected',
    )
  }
  return response
}
